import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { useCoiDiscovered } from "@/hooks/useCoiDiscovered";
import { useCoiLinkMutations } from "@/hooks/useCoiLinks";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_ROUTE: Record<string, string> = {
  "Client": "/clients",
  "Buyer Network": "/buyers",
  "Prospects": "/prospects",
  "COI": "/companies",
  "Capital Providers": "/capital-providers",
};

function timeAgo(iso: string) {
  const ms = Date.now() - new Date(iso).getTime();
  const d = Math.floor(ms / 86_400_000);
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d}d ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

interface Props { coiId: string }

export function CoiDiscoveredConnections({ coiId }: Props) {
  const { data, isLoading } = useCoiDiscovered(coiId);
  const { add } = useCoiLinkMutations(coiId);

  return (
    <div className="border rounded-lg p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Discovered Connections <span className="font-normal opacity-60">· from synced emails, last 90 days</span>
        </h3>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" /> Looking through email history…
        </div>
      ) : !data || data.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No email threads matched yet. Email sync runs hourly when you load any company page.
        </p>
      ) : (
        <ul className="divide-y">
          {data.map((d) => {
            const route = CATEGORY_ROUTE[d.companyCategory ?? ""] ?? "/clients";
            return (
              <li key={d.companyId} className="py-2 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <Link href={`${route}/${d.companyId}`} className="font-medium text-sm hover:underline">{d.companyName}</Link>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {d.threadCount} thread{d.threadCount === 1 ? "" : "s"} · last {timeAgo(d.lastTouchAt)}
                  </p>
                </div>
                <Button
                  size="sm" variant="outline" className="h-7 text-xs gap-1"
                  onClick={() =>
                    add.mutate(
                      { companyId: d.companyId, note: `Discovered via ${d.threadCount} email thread${d.threadCount === 1 ? "" : "s"}, promoted ${new Date().toISOString().slice(0, 10)}` },
                      { onSuccess: () => toast.success(`Linked ${d.companyName}`) },
                    )
                  }
                  disabled={add.isPending}
                >
                  <ArrowRight className="w-3 h-3" /> Link
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
